// Engine-side: keys, seed data, helpers.
// All user-facing words come from the active costume — see ./costumes/coworking.js.
// This file only carries STRUCTURE (stage keys, sample records). No industry vocabulary
// except inside seed-data fields, which the costume turns into screen labels.

import { COWORKING_COSTUME } from "./costumes/coworking";

// Re-export from the active costume so the rest of the engine has one place to import from.
export const ACTIVE_COSTUME = COWORKING_COSTUME;
export const STAGES = ACTIVE_COSTUME.stages;
export const STAGE_COLORS = ACTIVE_COSTUME.stageColors;
export const LOST_REASONS = ACTIVE_COSTUME.lostReasons;
export const ENQUIRY_TYPES = ACTIVE_COSTUME.enquiryTypes;
export const WA_TEMPLATES = ACTIVE_COSTUME.waTemplates;
export const COMPANY_INFO = ACTIVE_COSTUME.companyInfo;

// ---------- Org-level settings (per-organisation, NOT costume) ----------
// 11 Jun 2026: sources are configured by each organisation during setup;
// the costume only suggests defaults. This is the pilot org's locked list.
// Editable in the demo via Settings → Manage enquiry sources.
export const ORG_SOURCES = [
  "Walk-in",
  "Phone call",
  "Website",
  "Google maps",
  "Social media",
  "Others",
];

// ---------- Sample data ----------

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};
const daysAhead = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
};
// Timestamp helper for seeded remarks: n days back at a given hour.
const at = (daysBack, hour, minute = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
};

// 19 sample enquiries — all 7 stages covered, plus three scripted scenarios:
//   • e17  → 80-seat ask, triggers capacity-fit intelligence
//   • e18  → "Aarti Nair" phone matches no customer but COMPANY name "TechCo" matches
//            an existing footprint (expansion play)
//   • e19  → returning lead — same phone as a lost enquiry from months ago
// Emails are present on ~half the rows, mirroring the pilot's real data
// (email known for ~46% of historical enquiries).
export const SEED_ENQUIRIES = [
  { id: "e1", name: "Rakesh Kumar (DEMO)", phone: "09876543210", company: null, email: null,
    source: "Walk-in", enquiry_type: "Single seat", seat_count: 1,
    stage: "new", next_followup_date: daysAhead(0),
    notes: "Walked in this morning, working in fintech. Interested in single seat.",
    created_at: daysAgo(0), assigned_to: "Anita" },
  { id: "e2", name: "Priya Sharma (DEMO)", phone: "09812345678", company: "Sharma Designs (DEMO)",
    email: "priya.demo@example.com",
    source: "Website", enquiry_type: "Open seats (team)", seat_count: 5,
    stage: "new", next_followup_date: daysAhead(1),
    notes: "5-person dev team relocating from another city.",
    created_at: daysAgo(0), assigned_to: "Anita" },
  { id: "e3", name: "Vikram Singh (DEMO)", phone: "09823456789", company: null, email: null,
    source: "Phone call", enquiry_type: "Cabin", seat_count: 4,
    location_id: "vijay", location_name: "Vijay Hub",
    stage: "spoke", next_followup_date: daysAgo(1),
    notes: "Referred by Meera. Wants a private cabin for 4.",
    remarks: [
      { id: "r1", text: "Busy today — asked to call back tomorrow.", at: at(2, 16, 10), stage: "spoke", by: "Anita" },
      { id: "r2", text: "Needs to check with his partner. Call after 2 days.", at: at(1, 11, 30), stage: "spoke", by: "Anita" },
    ],
    created_at: daysAgo(3), assigned_to: "Anita" },
  { id: "e4", name: "Anjali Mehta (DEMO)", phone: "09834567890", company: null,
    email: "anjali.demo@example.com",
    source: "Website", enquiry_type: "Open seats (team)", seat_count: 12,
    stage: "visit_scheduled", next_followup_date: daysAhead(1),
    notes: "Visit booked for tomorrow 11am. 12-seat team.",
    created_at: daysAgo(5), assigned_to: "Ravi" },
  { id: "e5", name: "Suresh Reddy (DEMO)", phone: "09845678901", company: null, email: null,
    source: "Phone call", enquiry_type: "Single seat", seat_count: 1,
    stage: "visited", next_followup_date: daysAgo(2),
    notes: "Came in last week. Liked the space. Waiting on his decision.",
    remarks: [
      { id: "r3", text: "Liked the space but felt the 2nd floor was noisy. Thinking it over.", at: at(2, 17, 0), stage: "visited", by: "Anita" },
    ],
    created_at: daysAgo(8), assigned_to: "Anita" },
  { id: "e6", name: "Neha Joshi (DEMO)", phone: "09856789012", company: null,
    email: "neha.demo@example.com",
    source: "Google maps", enquiry_type: "Cabin", seat_count: 6,
    location_id: "navarang", location_name: "Navarang Hub",
    stage: "sent_price", next_followup_date: daysAgo(4),
    notes: "Quoted ₹45,000/mo for 6-seat cabin. No reply yet.",
    remarks: [
      { id: "r4", text: "Asked for 10% discount — checking with her finance head.", at: at(3, 12, 15), stage: "sent_price", by: "Ravi" },
    ],
    budget: 45000, created_at: daysAgo(10), assigned_to: "Ravi" },
  { id: "e7", name: "Karthik Iyer (DEMO)", phone: "09867890123", company: null, email: null,
    source: "Google maps", enquiry_type: "Open seats (team)", seat_count: 8,
    stage: "sent_price", next_followup_date: daysAhead(0),
    notes: "Quoted ₹60,000/mo for 8 open seats. Said he'll confirm by today.",
    budget: 60000, created_at: daysAgo(12), assigned_to: "Anita" },
  { id: "e8", name: "Meera Patel (DEMO)", phone: "09878901234", company: null,
    email: "meera.demo@example.com",
    source: "Others", enquiry_type: "Single seat", seat_count: 1,
    stage: "signed_up", deal_value: 18000, closed_by: "Anita",
    notes: "Networking-group referral. Closed in 2 weeks.",
    created_at: daysAgo(15), signed_up_at: daysAgo(2), assigned_to: "Anita" },
  { id: "e9", name: "Arjun Nair (DEMO)", phone: "09889012345", company: "TechCo (DEMO)",
    email: "arjun.demo@example.com",
    source: "Others", enquiry_type: "Cabin", seat_count: 8,
    stage: "signed_up", deal_value: 85000, closed_by: "Ravi",
    notes: "Referral from existing client. Big cabin deal.",
    created_at: daysAgo(22), signed_up_at: daysAgo(5), assigned_to: "Ravi" },
  { id: "e10", name: "Deepak Verma (DEMO)", phone: "09890123456", company: null, email: null,
    source: "Walk-in", enquiry_type: "Open seats (team)", seat_count: 4,
    stage: "signed_up", deal_value: 42000, closed_by: "Anita",
    notes: "Walked in, signed within a week.",
    created_at: daysAgo(35), signed_up_at: daysAgo(20), assigned_to: "Anita" },
  { id: "e11", name: "Sunita Rao (DEMO)", phone: "09801234567", company: null, email: null,
    source: "Social media", enquiry_type: "Single seat", seat_count: 1,
    stage: "lost", lost_reason: "Too costly",
    notes: "Felt our rate was too high.",
    created_at: daysAgo(30), assigned_to: "Ravi" },
  { id: "e12", name: "Rohan Das (DEMO)", phone: "09812340987", company: null, email: null,
    source: "Social media", enquiry_type: "Cabin", seat_count: 5,
    stage: "lost", lost_reason: "Went to a competitor",
    notes: "Picked a place closer to his home.",
    created_at: daysAgo(45), assigned_to: "Anita" },
  // Historical for chart/donut depth
  { id: "e13", name: "Asha Krishnan (DEMO)", phone: "09823450876", company: null,
    email: "asha.demo@example.com",
    source: "Others", enquiry_type: "Cabin", seat_count: 7,
    stage: "signed_up", deal_value: 78000, closed_by: "Ravi",
    notes: "Long-running deal, finally closed.",
    created_at: daysAgo(70), signed_up_at: daysAgo(55), assigned_to: "Ravi" },
  { id: "e14", name: "Manish Tiwari (DEMO)", phone: "09834560765", company: null, email: null,
    source: "Website", enquiry_type: "Open seats (team)", seat_count: 6,
    stage: "signed_up", deal_value: 35000, closed_by: "Anita",
    notes: "Web lead from 3 months ago.",
    created_at: daysAgo(100), signed_up_at: daysAgo(85), assigned_to: "Anita" },
  // Lapsed-lead-resurrection target: 5-seater wanted, was lost for 'no space of right size'.
  { id: "e15", name: "Pooja Bhatia (DEMO)", phone: "09845670654", company: null, email: null,
    source: "Google maps", enquiry_type: "Cabin", seat_count: 5,
    stage: "lost", lost_reason: "Found own place",
    notes: "Wanted a 5-seater cabin. Nothing available at the time.",
    created_at: daysAgo(80), assigned_to: "Anita" },
  { id: "e16", name: "Hari Menon (DEMO)", phone: "09856780543", company: null, email: null,
    source: "Others", enquiry_type: "Single seat", seat_count: 1,
    stage: "lost", lost_reason: "Stopped responding",
    notes: "Went cold after first call.",
    created_at: daysAgo(110), assigned_to: "Ravi" },
  // ---- Scripted intelligence triggers ----
  // 80-seat ask → triggers capacity-fit "no single hub fits, here's the phased option"
  { id: "e17", name: "Sunrise Innovations (DEMO)", phone: "09811223344", company: "Sunrise Innovations Pvt Ltd (DEMO)",
    email: "sunrise.demo@example.com",
    source: "Phone call", enquiry_type: "Open seats (team)", seat_count: 80,
    stage: "spoke", next_followup_date: daysAhead(1),
    notes: "Big ask — 80 seats. Wants to move in within 60 days. Open to phased.",
    created_at: daysAgo(1), assigned_to: "Anita" },
  // Company name matches TechCo (existing client) → expansion play
  { id: "e18", name: "Aarti Nair (DEMO)", phone: "09812349999", company: "TechCo (DEMO)",
    email: "aarti.demo@example.com",
    source: "Phone call", enquiry_type: "Open seats (team)", seat_count: 6,
    stage: "new", next_followup_date: daysAhead(0),
    notes: "Says she works at TechCo, wants 6 more seats.",
    created_at: daysAgo(0), assigned_to: "Anita" },
  // Phone matches Pooja Bhatia's old lost enquiry (returning lead).
  { id: "e19", name: "Pooja Bhatia (DEMO) — returned", phone: "09845670654", company: null, email: null,
    source: "Walk-in", enquiry_type: "Cabin", seat_count: 5,
    stage: "new", next_followup_date: daysAhead(0),
    notes: "Walked back in. Said her plans had paused; now ready.",
    created_at: daysAgo(0), assigned_to: "Anita" },
];
